/**
 * BudgetService
 * Main service for budget headers: CRUD, lifecycle actions, summary and ledger paging.
 * Route: api/budgets
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for budget CRUD plus the lifecycle / summary / transaction actions.
 * CRUD (getAll/getById/create/update/delete) is inherited from CrudService.
 */
export class BudgetService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? BudgetService.DEFAULT_SERVICE_PATH,
            basePath: BudgetService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- filtered list ----
    /** Filtered list of budget views (typeId / departmentId / projectId / statusId / effectiveOn / search). */
    async getFiltered(filter) {
        const response = await this.getFilteredRaw(filter);
        return response.data ?? [];
    }
    getFilteredRaw(filter) {
        return this.http.get(this.buildUrlWithParams(undefined, {
            typeId: filter?.typeId,
            departmentId: filter?.departmentId,
            projectId: filter?.projectId,
            statusId: filter?.statusId,
            effectiveOn: filter?.effectiveOn,
            search: filter?.search,
        }));
    }
    // ---- lifecycle ----
    /** Submit a Draft budget for approval (generates the budget code). */
    async submit(id) {
        const response = await this.submitRaw(id);
        if (!response.data)
            throw new Error(`Failed to submit budget ${id}`);
        return response.data;
    }
    submitRaw(id) {
        return this.http.put(this.buildUrl(`${id}/submit`), {});
    }
    /** Approve a Submitted budget (approver role). */
    async approve(id, body) {
        const response = await this.approveRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to approve budget ${id}`);
        return response.data;
    }
    approveRaw(id, body) {
        return this.http.put(this.buildUrl(`${id}/approve`), body ?? {});
    }
    /** Reject a Submitted budget — comments are REQUIRED. */
    async reject(id, body) {
        const response = await this.rejectRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to reject budget ${id}`);
        return response.data;
    }
    rejectRaw(id, body) {
        return this.http.put(this.buildUrl(`${id}/reject`), body);
    }
    /** Close an Approved budget (period over / manually retired). */
    async close(id, body) {
        const response = await this.closeRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to close budget ${id}`);
        return response.data;
    }
    closeRaw(id, body) {
        return this.http.put(this.buildUrl(`${id}/close`), body ?? {});
    }
    /** Cancel a budget (owner/admin). */
    async cancel(id, body) {
        const response = await this.cancelRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to cancel budget ${id}`);
        return response.data;
    }
    cancelRaw(id, body) {
        return this.http.put(this.buildUrl(`${id}/cancel`), body ?? {});
    }
    /** Amend an Approved budget (approver-gated; may re-arm alerts). */
    async amend(id, body) {
        const response = await this.amendRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to amend budget ${id}`);
        return response.data;
    }
    amendRaw(id, body) {
        return this.http.put(this.buildUrl(`${id}/amend`), body);
    }
    /** Reassign owner and/or the full manager list (Admin/BudgetAdmin only). */
    async reassign(id, body) {
        const response = await this.reassignRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to reassign budget ${id}`);
        return response.data;
    }
    reassignRaw(id, body) {
        return this.http.put(this.buildUrl(`${id}/reassign`), body);
    }
    // ---- project bridge ----
    /** Create a project budget (type 2, bottom-up) from the project's planned items and submit it. */
    async createFromProject(body) {
        const response = await this.createFromProjectRaw(body);
        if (!response.data)
            throw new Error('Failed to create budget from project');
        return response.data;
    }
    createFromProjectRaw(body) {
        return this.http.post(this.buildUrl('from-project'), body);
    }
    // ---- computed views ----
    /** Computed ledger position (header + per-allocation Committed/Actual/Available). */
    async getSummary(id) {
        const response = await this.getSummaryRaw(id);
        if (!response.data)
            throw new Error(`Summary for budget ${id} not found`);
        return response.data;
    }
    getSummaryRaw(id) {
        return this.http.get(this.buildUrl(`${id}/summary`));
    }
    /**
     * Every change ever made to this budget's total, oldest first (v3).
     *
     * The total is mutated in place by amendments and approved change requests, so without this
     * a budget that grew from R200k to R450k across four amendments looks identical to one that
     * was always R450k.
     */
    async getAmendments(id) {
        const response = await this.getAmendmentsRaw(id);
        return response.data ?? [];
    }
    getAmendmentsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/amendments`));
    }
    /** One page of the budget's transaction ledger. */
    async getTransactions(id, page = 1, pageSize = 50) {
        const response = await this.getTransactionsRaw(id, page, pageSize);
        if (!response.data)
            throw new Error(`Transactions for budget ${id} not found`);
        return response.data;
    }
    getTransactionsRaw(id, page = 1, pageSize = 50) {
        return this.http.get(this.buildUrlWithParams(`${id}/transactions`, { page, pageSize }));
    }
}
BudgetService.DEFAULT_SERVICE_PATH = 'budgets';
BudgetService.BASE_PATH = 'api/budgets';
//# sourceMappingURL=BudgetService.js.map